import { SidePanelDetailSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelDetailSectionTemplate";
import { useFreefieldState } from "components/Freefields/hooks";
import { GoogleMapLocation } from "components/GoogleMap/GoogleMapLocation";
import { ViewOnMap } from "components/GoogleMap/ViewOnMap";
import React, { useEffect, useState } from "react";
import { mapShowToShowInfo } from "show/edit/utils";
import { freeFieldEditableSections, getFreefieldId, isFreefieldKey } from "show/components/freeFields/freeFieldEditableSections";
import { artistSection } from "show/detail/sections/artistSection";
import { hotelSection } from "show/detail/sections/hotelSection";
import { mainSection } from "show/detail/sections/mainSection";
import { promoterSection } from "show/detail/sections/promoterSection";
import { ticketsSection } from "show/detail/sections/ticketsSection";
import { venueSection } from "show/detail/sections/venueSection";
import { ShowInfo } from "show/detail/utils";
import { invalidateShowsCache } from "utilities/api/showQuery";
import { useQueryClient } from "@tanstack/react-query";

interface ShowSidePanelDetailsProps {
  data: server.dto.ShowSidePanelData
  canEdit: boolean;
}

export const ShowSidePanelDetails = (props: ShowSidePanelDetailsProps) => {
  const isInternalUser = site.currentEnvironment.type === "internal";
  const isExternal = !isInternalUser;

  const [show, setShow] = useState<server.dto.Show>(props.data.show);
  const [showInfo, setShowInfo] = useState<ShowInfo>(mapShowToShowInfo(props.data.show));
  const [tags, setTags] = useState<server.dto.TagSmall[]>(props.data.show.tags || []);
  const [promoterContact, setPromoterContact] = useState<server.dto.Contact>(props.data.promoterContact);
  const [venueContact, setVenueContact] = useState<server.dto.Contact>(props.data.venueContact);
  const [hotelContact, setHotelContact] = useState<server.dto.Contact>(props.data.hotelContact);

  const ffState = useFreefieldState(props.data.show.freeFields, props.data.freeFieldCategories, props.data.freeFieldDefinitionPermissions);

  useEffect(() => {
    setShow(props.data.show);
    setShowInfo(mapShowToShowInfo(props.data.show));
    setTags(props.data.show.tags || []);
    setPromoterContact(props.data.promoterContact);
    setVenueContact(props.data.venueContact);
    setHotelContact(props.data.hotelContact);
  }, [props.data]);

  const queryClient = useQueryClient();

  const onValueChanged = async (key: string, value: any) => {

    if (isFreefieldKey(key)) {
      ffState.setEditableFreefieldValue(getFreefieldId(key), value);
      return;
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "artist") {
      setShowInfo(prev => ({ ...prev, artist: value, attendees: [] }));
    } else if (key === "venue") {
      setShowInfo(prev => ({ ...prev, venue: value, venueContact: null, capacity: null, doorsClose: null, doorsOpen: null, ageRestriction: null }));
      setVenueContact(null);
    } else if (key === "promoter") {
      setShowInfo(prev => ({ ...prev, promoter: value, promoterContact: null }));
      setPromoterContact(null);
    } else if (key === "hotel") {
      setShowInfo(prev => ({ ...prev, hotel: value, hotelContact: null }));
      setHotelContact(null);
    } else if (key === "ageRestriction") {
      setShowInfo(prev => ({ ...prev, ageRestriction: value?.value ?? value }));
    } else if (key === "currencyAndExchangeRate") {
      setShowInfo(prev => ({ ...prev, currency: value.currency, exchangeRate: value.exchangeRate }));
    } else if (key === "promoterContact") {
      setPromoterContact(value);
      setShowInfo(prev => ({ ...prev, promoterContact: value }));
    } else if (key === "hotelContact") {
      setHotelContact(value);
      setShowInfo(prev => ({ ...prev, hotelContact: value }));
    } else if (key === "venueContact") {
      setVenueContact(value);
      setShowInfo(prev => ({ ...prev, venueContact: value }));
    } else {
      setShowInfo(prev => ({ ...prev, [key]: value }));
    }
  };

  const onSuccessfulUpdate = async (key: string, newValue: any) => {
    if (isFreefieldKey(key)) {
      return;
    }

    if (key === "promoterContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.promoter.companyId, contactId: newValue.contactId, isActive: true });
    } else if (key === "hotelContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.hotel.companyId, contactId: newValue.contactId, isActive: true });
    } else if (key === "venueContact" && newValue != null) {
      window.CoreApi.CompanyToContact.SetLink({ companyId: showInfo.venue.venueId, contactId: newValue.contactId, isActive: true });
    }

    const res = await window.CoreApi.Show.GetSidePanelData(show.showId, { toastOnError: true });
    if (!res.data.hasErrors) {
      const sidePanelData = res.data.data;
      setShow(sidePanelData.show);
      setShowInfo(mapShowToShowInfo(sidePanelData.show));
      setTags(sidePanelData.show.tags || []);
      setPromoterContact(sidePanelData.promoterContact);
      setVenueContact(sidePanelData.venueContact);
      setHotelContact(sidePanelData.hotelContact);
    }
    invalidateShowsCache(queryClient);
  };

  const canInPageEdit = props.canEdit && show.permissions.canModify;

  return <SidePanelDetailSectionTemplate
    isInPageEditable={canInPageEdit}
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    fields={[

      mainSection(show, showInfo, tags, isExternal, true),
      artistSection(show, showInfo, isExternal, true),
      venueSection(show, showInfo, venueContact, isExternal, true),
      promoterSection(show, showInfo, promoterContact, isExternal, true),
      hotelSection(show, showInfo, hotelContact, isExternal, true),
      ticketsSection(show, showInfo, isExternal),
      ...freeFieldEditableSections(
        show.showId,
        ffState.freefieldCategoriesWithValuesAndPermissions,
        window.CoreApi.Show.UpdateFreeField,
      ),
    ]}
    additionalContent={<>
      {show.venue?.location && <>
        <ViewOnMap height={400}>
          <GoogleMapLocation
            pin={{
              type: "show",
              phase: "unknown"
            }}
            location={show.venue.location}
          />
        </ViewOnMap>
      </>}
    </>}
  />
}
